import { useState } from 'react'
import { ReviewMetrics } from '../../../types/metrics'
import InfoTooltip from '../../atoms/InfoTooltip'

type Props = {
  metrics: ReviewMetrics
}

type Tile = {
  label: string
  count: number
  bgColor: string
  textColor: string
  sub: string
  tooltip: string
}

const TalentRankGrid = ({ metrics }: Props): JSX.Element => {
  const [hoveredTile, setHoveredTile] = useState<string | null>(null)
  const total = metrics.talentRankTenX + metrics.talentRankNineX + metrics.talentRankCore + metrics.talentRankMismatch

  const pct = (n: number): string => (total > 0 ? `${Math.round((n / total) * 100)}%` : '–')

  const tiles: Tile[] = [
    {
      label: '10X',
      count: metrics.talentRankTenX,
      bgColor: '#15803D',
      textColor: '#FFFFFF',
      sub: pct(metrics.talentRankTenX),
      tooltip: 'Top talent — exceptional contributors who multiply the productivity of those around them.',
    },
    {
      label: '9X',
      count: metrics.talentRankNineX,
      bgColor: '#22C55E',
      textColor: '#FFFFFF',
      sub: pct(metrics.talentRankNineX),
      tooltip: 'Growth / Expert talent — strong performers on an upward trajectory or deep domain specialists.',
    },
    {
      label: 'Core',
      count: metrics.talentRankCore,
      bgColor: '#FEF9C3',
      textColor: '#854D0E',
      sub: pct(metrics.talentRankCore),
      tooltip: 'Core talent — solid, reliable contributors who meet expectations and form the backbone of the team.',
    },
    {
      label: 'Mismatch',
      count: metrics.talentRankMismatch,
      bgColor: '#FEE2E2',
      textColor: '#B91C1C',
      sub: pct(metrics.talentRankMismatch),
      tooltip:
        'Mismatched talent — individuals whose current role or performance may not align with expectations. Requires attention.',
    },
  ]

  return (
    <div className="rounded-2xl bg-white p-8 shadow-[0_0_20px_5px_rgba(0,0,0,0.08)]">
      <div className="mb-4 flex items-center gap-2">
        <h3 className="text-2xl font-bold text-dark-blue">Talent rank distribution</h3>
        <InfoTooltip text="Share of reports placed into each talent rank (10X, 9X, Core, Mismatch) within the selected time range." />
      </div>
      <div className="grid grid-cols-2 gap-3" role="list" aria-label="Talent rank distribution">
        {tiles.map(tile => (
          <div
            key={tile.label}
            role="listitem"
            aria-label={`${tile.label}: ${tile.count} reviews (${tile.sub})`}
            className="relative flex flex-col items-center justify-center rounded-xl p-5"
            style={{ backgroundColor: tile.bgColor, color: tile.textColor }}
            onMouseEnter={() => setHoveredTile(tile.label)}
            onMouseLeave={() => setHoveredTile(null)}
          >
            <span className="text-4xl font-bold">{tile.count}</span>
            <span className="mt-1 text-xl font-semibold">{tile.label}</span>
            <span className="mt-0.5 text-xl opacity-80">{tile.sub} of reviewed</span>
            {hoveredTile === tile.label && (
              <div
                role="tooltip"
                className="absolute -bottom-2 left-1/2 z-10 w-56 -translate-x-1/2 translate-y-full rounded-lg border border-soft-gray bg-white p-3 text-xl font-normal leading-relaxed text-stone-gray shadow-lg"
              >
                {tile.tooltip}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  )
}

export const TalentRankGridSkeleton = (): JSX.Element => (
  <div aria-hidden="true" className="rounded-2xl bg-white p-8 shadow-[0_0_20px_5px_rgba(0,0,0,0.08)]">
    <div className="mb-4 h-7 w-2/3 rounded bg-soft-gray animate-pulse" />
    <div className="grid grid-cols-2 gap-3">
      {[0, 1, 2, 3].map(i => (
        <div key={i} className="h-32 rounded-xl bg-soft-gray animate-pulse" />
      ))}
    </div>
  </div>
)

export default TalentRankGrid
